import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  Users,
  X,
  Loader2,
  FilePlus,
  LinkIcon,
  Mail,
  Search,
  ArrowLeft,
  FileText,
  ChevronDown,
} from "lucide-react";
import {
  useCreateCompany,
  useUpdateCompany,
} from "@/hooks/mutations/companies";
import { useAllRecruiters } from "@/hooks/queries/recruiters";
import { useCompanyById } from "@/hooks/queries/useCompanies";
import { GoogleFontLoader } from "@/components/GoogleFontLoader";
import Dropdown from "@/components/ui/Dropdown";
import Loader from "@/components/Loader";
import { companiesService } from "@/services/companiesService";
import { googleFontsApiKey } from "@/config";
import axios from "axios";
import useToastStore from "@/components/Common/Toast/toastStore";
import useAuth from "@/hooks/stores/useAuth";

export default ({ hideHeader, companyId: propCompanyId }) => {
  const navigate = useNavigate();
  const { id: paramId } = useParams();
  const location = useLocation();
  const { showToast } = useToastStore;
  const { userRole, currentUser } = useAuth();
  // Resolve the companyId from the prop or URL parameter.
  const companyId = propCompanyId || paramId;

  // Initialize state with keys that match the API response.
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    logo: "",
    website: "",
    email_alias: "",
    recruiters: [],
    brand_color: "#64B2A4", // Default to your current primary color
    brand_font: "Roboto", // Default font
    bullhorn_company_id: "", // Add bullhorn_company_id field
  });
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [touched, setTouched] = useState({
    name: false,
    logo: false,
    website: false,
    recruiters: false,
    email_alias: false,
    brand_color: false,
    brand_font: false,
    bullhorn_company_id: false, // Add bullhorn_company_id to touched state
  });
  const [recruiterSearch, setRecruiterSearch] = useState("");
  const [isRecruiterDropdownOpen, setIsRecruiterDropdownOpen] = useState(false);
  const [isFontDropdownOpen, setIsFontDropdownOpen] = useState(false);
  const [fontOptions, setFontOptions] = useState([]);
  const [isLoadingFonts, setIsLoadingFonts] = useState(true);
  const [fontSearchQuery, setFontSearchQuery] = useState("");
  const [filteredFonts, setFilteredFonts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [fontCategories, setFontCategories] = useState([]);
  // Fetch recruiters dynamically
  const { data: recruiters = [], isLoading: isRecruitersLoading } =
    useAllRecruiters();
  const { data: companyData, isLoading: isCompanyLoading } = useCompanyById(
    propCompanyId || paramId,
  );

  // Fetch company data if editing
  useEffect(() => {
    // console.log("Company ID in useEffect:", companyId);
    if (companyData) {
      const fetchCompanyData = async () => {
        try {
          const response = await companiesService.getCompanyById(companyId);
          // console.log("Fetched company data:", response);
          const companyData = response.company;

          // Set the form state using data returned from the API.
          // Note that recruiters come from the top-level response.
          setFormData({
            name: companyData.name || "",
            description: companyData.description || "",
            logo: companyData.logo || "",
            website: companyData.website || "",
            email_alias: companyData.email || "",
            brand_color: companyData.brand_color || "#64B2A4",
            brand_font: companyData.brand_font || "Roboto",
            recruiters: response.recruiters || [], // Recruiters specific to the company.
            bullhorn_company_id: companyData.bullhorn_company_id || "", // Set bullhorn_company_id
          });

          // Set logo preview if logo is a file object else use string URL.
          if (companyData.logo && typeof companyData.logo === "object") {
            setLogoPreview(companyData.logo.url || "");
          } else {
            setLogoPreview(companyData.logo);
          }
        } catch (error) {
          console.error("Failed to fetch company data:", error);
          showToast({
            message: "Failed to fetch company data.",
            type: "error",
          });
        }
      };

      fetchCompanyData();
    }
  }, [companyId]);

  // Add this useEffect hook at the top of the component
  useEffect(() => {
    const handleClickOutside = (event) => {
      const dropdown = document.querySelector(".recruiter-dropdown");
      const input = document.querySelector(".recruiter-input");

      if (
        dropdown &&
        input &&
        !dropdown.contains(event.target) &&
        !input.contains(event.target)
      ) {
        setIsRecruiterDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Fetch Google Fonts
  useEffect(() => {
    const fetchGoogleFonts = async () => {
      try {
        setIsLoadingFonts(true);
        // Replace API_KEY with your actual Google Fonts API key
        const response = await axios.get(
          `https://www.googleapis.com/webfonts/v1/webfonts?key=${googleFontsApiKey}&sort=popularity`,
        );

        if (response.data && response.data.items) {
          const fonts = response.data.items.slice(0, 100); // Limit to first 100 fonts for performance

          // Structure fonts data for dropdown
          const formattedFonts = fonts.map((font) => ({
            label: font.family,
            value: font.family,
            category: font.category,
          }));

          setFontOptions(formattedFonts);

          // Extract unique categories
          const categories = [
            "all",
            ...new Set(fonts.map((font) => font.category)),
          ];
          setFontCategories(categories);
        }
      } catch (error) {
        console.error("Error fetching Google Fonts:", error);
        // Fallback to static font list if API call fails
        const fallbackFonts = [
          // 'Roboto', 'Open Sans', 'Lato', 'Montserrat', 'Poppins',
          // 'Raleway', 'Nunito', 'Playfair Display', 'Source Sans Pro', 'Ubuntu'
          "Roboto",
          "Open Sans",
          "Lato",
          "Montserrat",
          "Poppins",
          "Raleway",
          "Nunito",
          "Playfair Display",
          "Source Sans Pro",
          "Ubuntu",
          "Merriweather",
          "Roboto Condensed",
          "Roboto Slab",
          "PT Sans",
          "Oswald",
          "Noto Sans",
          "Roboto Mono",
          "Work Sans",
          "Quicksand",
          "Inter",
          "Mulish",
          "Fira Sans",
          "Nunito Sans",
          "Rubik",
          "DM Sans",
          "Josefin Sans",
          "Barlow",
          "Karla",
          "Space Grotesk",
          "Cabin",
          "Hind",
          "Inconsolata",
          "PT Serif",
          "Zilla Slab",
          "Heebo",
          "Titillium Web",
          "Varela Round",
          "Arimo",
          "Mukta",
          "Crimson Text",
          "Bebas Neue",
          "Overpass",
          "Manrope",
          "Teko",
          "Libre Baskerville",
          "Domine",
          "Muli",
          "Exo 2",
          "Prompt",
          "Signika",
          "Archivo",
          "Asap",
          "Cantarell",
          "Eczar",
          "Nanum Gothic",
          "Questrial",
          "Archivo Narrow",
          "Anton",
          "Noto Serif",
          "Fjalla One",
          "Dosis",
          "Kanit",
          "PT Sans Narrow",
          "Cairo",
          "Tajawal",
          "Barlow Condensed",
          "Amatic SC",
          "Cormorant Garamond",
          "Jost",
          "Abel",
          "Shadows Into Light",
          "Lora",
          "Martel",
          "Vollkorn",
          "Frank Ruhl Libre",
          "Khand",
          "Rokkitt",
          "Maven Pro",
          "EB Garamond",
          "Alegreya",
          "Cardo",
          "IBM Plex Sans",
          "Baloo 2",
          "Prata",
          "Alegreya Sans",
          "Public Sans",
          "Exo",
          "Coda",
          "Arvo",
          "Overlock",
          "Bitter",
          "Great Vibes",
          "Concert One",
          "Yanone Kaffeesatz",
          "Philosopher",
          "Tangerine",
          "Crete Round",
          "Gudea",
          "Saira",
          "Noticia Text",
          "Hammersmith One",
          "Fredoka One",
          "Righteous",
          "Patua One",
          "Cuprum",
          "Special Elite",
          "Pacifico",
          "Allura",
          "Gloria Hallelujah",
          "Courgette",
          "Dancing Script",
          "Sacramento",
          "Handlee",
          "Kalam",
          "Indie Flower",
          "Yellowtail",
          "Marcellus",
          "Old Standard TT",
          "Josefin Slab",
          "Bangers",
          "Permanent Marker",
          "Carter One",
          "Orbitron",
          "Luckiest Guy",
          "Abril Fatface",
          "Unica One",
          "Lilita One",
          "Poiret One",
          "Satisfy",
          "Chewy",
          "Cinzel",
          "Changa",
          "Baloo Tamma 2",
          "Viga",
          "Ropa Sans",
          "Be Vietnam",
          "Russo One",
          "Cookie",
          "Itim",
        ];

        setFontOptions(
          fallbackFonts.map((font) => ({
            label: font,
            value: font,
            category: "sans-serif",
          })),
        );

        setFontCategories(["all", "sans-serif"]);
      } finally {
        setIsLoadingFonts(false);
      }
    };

    fetchGoogleFonts();
  }, []);

  // Filter fonts based on search query and category
  useEffect(() => {
    let filtered = fontOptions.map((font) => ({
      family: font,
      category: "sans-serif", // Default category if not available
    }));

    // Filter by search query
    if (fontSearchQuery) {
      filtered = filtered.filter((font) =>
        font.family.toLowerCase().includes(fontSearchQuery.toLowerCase()),
      );
    }

    // Filter by category
    if (selectedCategory && selectedCategory !== "all") {
      filtered = filtered.filter((font) => font.category === selectedCategory);
    }

    setFilteredFonts(filtered);
  }, [fontSearchQuery, selectedCategory, fontOptions]);

  // Update state when inputs change.
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({ ...prevState, [name]: value }));
  };

  // Handle logo file selection.
  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRecruiterSelect = (e) => {
    const selectedId = e.target.value;
    const selectedRecruiter = recruiters.find(
      (r) => r.id.toString() === selectedId,
    );
    if (
      selectedId &&
      selectedRecruiter &&
      !formData.recruiters.some(
        (r) => (typeof r === "object" ? r.id.toString() : r) === selectedId,
      )
    ) {
      setFormData((prev) => ({
        ...prev,
        recruiters: [...prev.recruiters, selectedRecruiter],
      }));
    }
    e.target.value = "";
  };

  const { mutate: createCompany } = useCreateCompany({
    onSuccess: (data) => {
      showToast({ message: "Company created successfully.", type: "success" });
      setIsSubmitting(false);
      // Assuming the response has data.company.id
      if (data && data.company && data.company.id) {
        // Navigate to the company view with the Campaign tab active
        navigate(`/companies/view/${data.company.id}`, {
          state: { activeTab: "campaigns" },
        });
      } else {
        // Fallback if id is not available.
        navigate("/companies");
      }
    },
    onError: (error) => {
      showToast({ message: error.message, type: "error" });
      console.error("Error creating company:", error);
      setIsSubmitting(false);
    },
  });

  const { mutate: updateCompany } = useUpdateCompany({
    onSuccess: () => {
      console.log("Company updated successfully.");
      showToast({ message: "Company updated successfully.", type: "success" });
    },
    onError: (error) => {
      showToast({ message: "Error updating company.", type: "error" });
      console.error("Error updating company:", error);
    },
  });

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Company name is required.";
    }

    if (!logoFile && !formData.logo) {
      newErrors.logo = "Company logo is required.";
    }

    if (
      formData.website.trim() &&
      !/^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/.test(
        formData.website,
      )
    ) {
      newErrors.website = "Invalid website URL format.";
    }

    if (
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email_alias) &&
      formData.email_alias.trim() !== ""
    ) {
      newErrors.email_alias = "Invalid email format.";
    }

    // if (formData.recruiters.length === 0) {
    // newErrors.recruiters = "At least one recruiter is required.";
    // }

    if (!formData.brand_color) {
      newErrors.brand_color = "Brand color is required.";
    }

    if (!formData.brand_font) {
      newErrors.brand_font = "Brand font is required.";
    }

    if (!formData.bullhorn_company_id.trim()) {
      newErrors.bullhorn_company_id = "Company ID is required.";
    } else if (!/^\d+$/.test(formData.bullhorn_company_id)) {
      newErrors.bullhorn_company_id = "Company ID must contain only numbers.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateField = (field) => {
    const newErrors = { ...errors };

    switch (field) {
      case "name":
        if (!formData.name.trim()) {
          newErrors.name = "Company name is required.";
        } else {
          delete newErrors.name;
        }
        break;

      case "bullhorn_company_id":
        if (!formData.bullhorn_company_id.trim()) {
          newErrors.bullhorn_company_id = "Company ID is required.";
        } else if (!/^\d+$/.test(formData.bullhorn_company_id)) {
          newErrors.bullhorn_company_id =
            "Company ID must contain only numbers.";
        } else {
          delete newErrors.bullhorn_company_id;
        }
        break;

      case "logo":
        if (!logoFile && !formData.logo) {
          newErrors.logo = "Company logo is required.";
        } else {
          delete newErrors.logo;
        }
        break;

      case "website":
        if (
          formData.website.trim() &&
          !/^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/.test(
            formData.website,
          )
        ) {
          newErrors.website = "Invalid website URL format.";
        } else {
          delete newErrors.website;
        }
        break;

      // case 'recruiters':
      //     if (formData.recruiters.length === 0) {
      //     newErrors.recruiters = "At least one recruiter is required.";
      //     } else {
      //     delete newErrors.recruiters;
      //     }
      //     break;

      case "email_alias":
        if (
          !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email_alias) &&
          formData.email_alias.trim() !== ""
        ) {
          newErrors.email_alias = "Invalid email format.";
        } else {
          delete newErrors.email_alias;
        }
        break;

      case "brand_color":
        if (!formData.brand_color || formData.brand_color === "#000000") {
          newErrors.brand_color = "Brand color is required.";
        } else {
          delete newErrors.brand_color;
        }
        break;

      case "brand_font":
        if (!formData.brand_font) {
          newErrors.brand_font = "Brand font is required.";
        } else {
          delete newErrors.brand_font;
        }
        break;

      default:
        break;
    }

    setErrors(newErrors);
    return !newErrors[field];
  };

  const handleBlur = (field) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    validateField(field);
  };

  // Handle form submission.
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);

    const formDataToSend = new FormData();
    formDataToSend.append("name", formData.name);
    formDataToSend.append("bullhorn_company_id", formData.bullhorn_company_id);
    formDataToSend.append("description", formData.description);
    formDataToSend.append("email", formData.email_alias);
    formDataToSend.append("website", formData.website);
    formDataToSend.append("brand_color", formData.brand_color);
    formDataToSend.append("brand_font", formData.brand_font);

    // Map recruiters to extract only the ids whether they're objects or strings.
    const recruiterIds = formData.recruiters.map((rec) =>
      typeof rec === "object" && rec !== null ? rec.id : rec,
    );
    formDataToSend.append("recruiters", recruiterIds.join(","));

    // Append logo only if a new file is selected.
    if (logoFile) {
      formDataToSend.append("logo", logoFile);
    }

    if (companyId) {
      // For updating via a POST method that should act as a PUT, append _method: 'PUT'
      formDataToSend.append("_method", "PUT");
      updateCompany(
        { companyId, companyData: formDataToSend },
        {
          onSuccess: () => {
            setIsSubmitting(false);
          },
          onError: () => {
            setIsSubmitting(false);
          },
        },
      );
    } else {
      createCompany(formDataToSend);
    }
  };

  const handleBack = (e) => {
    e.preventDefault(); // Prevent default form submission behavior
    e.stopPropagation(); // Stop event bubbling
    navigate(-1);
  };

  return (
    <>
      <GoogleFontLoader font={formData.brand_font} />
      <form onSubmit={handleSubmit} className="">
        {/* Current User Role */}

        {/* Company Details */}
        <div className="space-y-6">
          {/* Company Name */}
          <div className="">
            <label className="block text-sm font-semibold text-foreground mb-2">
              Company Name <span className="text-red-500">*</span>
            </label>
            <div className={`flex items-center gap-3 bg-muted/60 hover:bg-muted focus-within:bg-white focus-within:ring-2 focus-within:ring-[#18181B] transition-all rounded-xl p-4 ${
                  errors.name ? "ring-2 ring-red-500" : ""
                }`}>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={(e) => {
                  handleInputChange(e);
                  if (touched.name) {
                    validateField("name");
                  }
                }}
                onBlur={() => handleBlur("name")}
                className="bg-transparent w-full focus:outline-none text-foreground placeholder:text-muted-foreground"
                placeholder="Enter company name"
                required
              />
            </div>
            {errors.name && (
              <p className="text-red-500 text-sm mt-1">{errors.name}</p>
            )}
          </div>

          {/* Logo Upload */}
          <div className="">
            <label className="block text-sm font-semibold text-foreground mb-2">
              Company Logo <span className="text-red-500">*</span>
            </label>
            <div className="flex items-center gap-4">
              <input
                type="file"
                onChange={handleLogoChange}
                className="hidden"
                id="logo-upload"
                accept="image/*"
              />
              <label
                htmlFor="logo-upload"
                className="flex items-center cursor-pointer"
              >
                <div className="flex items-center justify-center h-12 px-6 bg-[#09090B] hover:bg-black transition-colors text-white rounded-xl font-medium">
                  <FilePlus className="w-5 h-5 mr-2" />
                  <span>Choose File</span>
                </div>
              </label>
              {logoPreview && (
                <div className="relative group">
                  <img
                    src={logoPreview}
                    alt="Logo Preview"
                    className="w-16 h-16 object-cover rounded-xl border border-border shadow-sm"
                  />
                </div>
              )}
            </div>
            {errors.logo && (
              <p className="text-red-500 text-sm mt-1">{errors.logo}</p>
            )}
          </div>

          {/* Company Bullhorn ID */}
          <div className="">
            <label className="block text-sm font-semibold text-foreground mb-2">
              Bullhorn ID <span className="text-red-500">*</span>
            </label>

            <div className={`flex items-center gap-3 bg-muted/60 hover:bg-muted focus-within:bg-white focus-within:ring-2 focus-within:ring-[#18181B] transition-all rounded-xl p-4 ${
                  errors.bullhorn_company_id ? "ring-2 ring-red-500" : ""
                }`}>
              <input
                type="number"
                name="bullhorn_company_id"
                value={formData.bullhorn_company_id}
                onChange={(e) => {
                  handleInputChange(e);
                  if (touched.bullhorn_company_id) {
                    validateField("bullhorn_company_id");
                  }
                }}
                onBlur={() => handleBlur("bullhorn_company_id")}
                className="bg-transparent w-full focus:outline-none text-foreground placeholder:text-muted-foreground"
                placeholder="Enter Company Bullhorn ID"
                min="0"
                required
                onKeyPress={(e) => {
                  if (!/[0-9]/.test(e.key)) {
                    e.preventDefault();
                  }
                }}
              />
            </div>
            {errors.bullhorn_company_id && (
              <p className="text-red-500 text-sm mt-1">
                {errors.bullhorn_company_id}
              </p>
            )}
          </div>

          {/* Website URL */}
          <div className="">
            <label className="block text-sm font-semibold text-foreground mb-2">Website URL</label>
            <div className={`flex items-center gap-3 bg-muted/60 hover:bg-muted focus-within:bg-white focus-within:ring-2 focus-within:ring-[#18181B] transition-all rounded-xl p-4 ${
                  errors.website ? "ring-2 ring-red-500" : ""
                }`}>
              <LinkIcon className="w-5 h-5 text-muted-foreground" />
              <input
                type="url"
                name="website"
                value={formData.website}
                onChange={(e) => {
                  handleInputChange(e);
                  if (touched.website) {
                    validateField("website");
                  }
                }}
                onBlur={() => handleBlur("website")}
                className="bg-transparent w-full focus:outline-none text-foreground placeholder:text-muted-foreground"
                placeholder="https://www.company.com"
              />
            </div>
            {errors.website && (
              <p className="text-red-500 text-sm mt-1">{errors.website}</p>
            )}
          </div>

          {/* Email */}
          <div className="">
            <label className="block text-sm font-semibold text-foreground mb-1">Email Address</label>
            <p className="text-muted-foreground text-xs mb-2">
              For internal record keeping and company contact.
            </p>
            <div className={`flex items-center gap-3 bg-muted/60 hover:bg-muted focus-within:bg-white focus-within:ring-2 focus-within:ring-[#18181B] transition-all rounded-xl p-4 ${
                  errors.email_alias ? "ring-2 ring-red-500" : ""
                }`}>
              <Mail className="w-5 h-5 text-muted-foreground" />
              <input
                type="email"
                name="email_alias"
                value={formData.email_alias}
                onChange={(e) => {
                  handleInputChange(e);
                  if (touched.email_alias) {
                    validateField("email_alias");
                  }
                }}
                onBlur={() => handleBlur("email_alias")}
                className="bg-transparent w-full focus:outline-none text-foreground placeholder:text-muted-foreground"
                placeholder="contact@company.com"
              />
            </div>
            {errors.email_alias && (
              <p className="text-red-500 text-sm mt-1">{errors.email_alias}</p>
            )}
          </div>
        </div>

        {/* Branding Section Title */}
        <div className="pt-6 border-t border-border mt-8 mb-6">
           <h3 className="text-xl font-bold text-foreground">Branding & Visuals</h3>
           <p className="text-muted-foreground text-sm mt-1">Customize how this company appears in the interface.</p>
        </div>

        <div className="space-y-6">
          {/* Brand Color */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="">
              <label className="block text-sm font-semibold text-foreground mb-2">
                Brand Color <span className="text-red-500">*</span>
              </label>
              <div className="flex items-center gap-3 bg-muted/60 hover:bg-muted transition-all rounded-xl p-4">
                <div
                  className="w-6 h-6 rounded-lg cursor-pointer border border-white shadow-sm"
                  style={{ backgroundColor: formData.brand_color }}
                  onClick={() =>
                    document.querySelector('input[name="brand_color"]').click()
                  }
                />
                <input
                  type="color"
                  name="brand_color"
                  value={formData.brand_color}
                  onChange={(e) => {
                    handleInputChange(e);
                    if (touched.brand_color) {
                      validateField("brand_color");
                    }
                  }}
                  onBlur={() => handleBlur("brand_color")}
                  className="w-full h-8 bg-transparent cursor-pointer"
                />
              </div>
              <p className="text-muted-foreground text-xs mt-2">Color: {formData.brand_color}</p>
              {errors.brand_color && (
                <p className="text-red-500 text-sm mt-1">{errors.brand_color}</p>
              )}
            </div>

            {/* Brand Font */}
            <div className="">
              <label className="block text-sm font-semibold text-foreground mb-2">
                Brand Font <span className="text-red-500">*</span>
              </label>

              <Dropdown
                options={fontOptions}
                value={{
                  label: formData.brand_font,
                  value: formData.brand_font,
                }}
                onChange={(selected) => {
                  setFormData((prev) => ({
                    ...prev,
                    brand_font: selected.value || selected.label,
                  }));
                  if (touched.brand_font) {
                    validateField("brand_font");
                  }
                }}
                placeholder="Select a font"
                icon={<FileText className="w-5 h-5 text-muted-foreground flex-shrink-0" />}
                searchable={true}
                categories={fontCategories}
                showCategories={false}
                renderOption={(option) => (
                  <p className="text-foreground">{option.label}</p>
                )}
                error={errors.brand_font}
              />
            </div>
          </div>
        </div>

        <div className="pt-6 border-t border-border mt-8 mb-6">
             <h3 className="text-xl font-bold text-foreground">Internal Settings</h3>
             <p className="text-muted-foreground text-sm mt-1">Configure internal assignments and company tracking.</p>
          </div>

          <div className="space-y-6">
            {/* Assign Recruiters */}
            <div className="">
              <label className="block text-sm font-semibold text-foreground mb-2">
                Assign Recruiters
              </label>
            {isRecruitersLoading ? (
              <Loader />
            ) : (
              <Dropdown
                options={recruiters
                  .filter((r) => r.id != currentUser?.user?.id)
                  ?.map((recruiter) => ({
                    label: recruiter.name,
                    value: recruiter.id,
                  }))}
                value={null}
                onChange={(selected) => {
                  if (
                    selected &&
                    !formData.recruiters.some(
                      (r) =>
                        (typeof r === "object" ? r.id : r) === selected.value,
                    )
                  ) {
                    setFormData((prev) => ({
                      ...prev,
                      recruiters: [
                        ...prev.recruiters,
                        recruiters.find((r) => r.id === selected.value),
                      ],
                    }));
                    if (touched.recruiters) {
                      validateField("recruiters");
                    }
                  }
                }}
                placeholder="Search recruiters..."
                icon={<Users className="w-5 h-5 text-muted-foreground flex-shrink-0" />}
                searchable={true}
                showCategories={false}
                error={errors.recruiters}
              />
            )}
          </div>

          {/* Display Selected Recruiters */}
          <div className="mt-2">
            <div className="flex flex-wrap gap-2">
              {currentUser && (
                <div className="flex items-center bg-muted text-foreground rounded-lg px-3 py-1.5 text-sm font-medium border border-border">
                  <Users className="w-4 h-4 inline-block mr-2 text-muted-foreground" />
                  {currentUser?.user?.name}
                </div>
              )}
              {formData.recruiters.map((recruiter) => (
                <div
                  key={recruiter.id}
                  className="flex items-center bg-muted text-foreground rounded-lg px-3 py-1.5 text-sm font-medium border border-border group"
                >
                  <Users className="w-4 h-4 inline-block mr-2 text-muted-foreground" />
                  {recruiter.name}
                  <button
                    type="button"
                    onClick={() =>
                      setFormData((prev) => ({
                        ...prev,
                        recruiters: prev.recruiters.filter(
                          (r) => r.id !== recruiter.id,
                        ),
                      }))
                    }
                    className="ml-2 text-muted-foreground hover:text-red-500 transition-colors"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-4 pt-8 border-t border-border mt-10">
          <button
            type="button"
            onClick={handleBack}
            className="flex-1 bg-card border border-border text-foreground hover:bg-muted py-4 px-6 rounded-xl transition-all duration-200 flex items-center justify-center gap-2 font-semibold shadow-sm"
          >
            <span>Cancel</span>
          </button>
          
          <button
            type="submit"
            disabled={isSubmitting}
            className="flex-1 bg-[#09090B] hover:bg-black text-white py-4 px-6 rounded-xl shadow-lg transition-all duration-200 flex items-center justify-center gap-2 font-semibold relative overflow-hidden group"
          >
            {isSubmitting ? (
              <div className="flex items-center gap-3">
                <Loader2 className="w-5 h-5 animate-spin text-white/70" />
                <span className="opacity-80">
                  {companyId ? "Updating..." : "Saving..."}
                </span>
              </div>
            ) : (
              <>
                <span>{companyId ? "Update Company" : "Save Company"}</span>
              </>
            )}
          </button>
        </div>
      </form>
    </>
  );
};

// export default Form;
